/**
 * App initialization for grizzly-web SPA.
 *
 * Called once when the page loads. Sets up the router, restores Galaxy state,
 * renders sidebar + atmosphere, and starts background polling.
 */

import { initRouter, navigate } from './router.js'
import { initCommandPalette, openCommandPalette } from './components/commandPalette.js'
import { initOtel, flushOtel, reportUnhandledError } from './otel.js'
import { showToast, esc, renderMd, relTime, codeDiff } from './components/shared.js'
import { renderSidebar, toggleGalaxy, restoreGalaxy } from './galaxy/components/sidebar.js'
import { renderTopbar } from './galaxy/components/topbar.js'
import { renderAtmosphere } from './galaxy/components/atmosphere.js'
import { togSec } from './galaxy/components/section.js'
import { closeDrawer, switchDrawerTab } from './galaxy/components/drawer.js'
import { copyToClip } from './galaxy/components/copyButton.js'
import { initKeyboardShortcuts } from './galaxy/components/keyboard.js'
import { initActionDelegation } from './galaxy/components/action-delegation.js'
import { registerAllActions } from './galaxy/components/action-registry.js'
import { startFleetSSE, stopFleetSSE } from './fleet-stream.js'
import {
  setWorkTypeFilter,
  setWorkStatusFilter,
  setWorkPriorityFilter,
  setWorkAssignee,
  clearWorkFilters,
  setWorkPage,
  toggleWorkTag,
  setWorkSort,
  setWorkSearchInput,
} from './galaxy/views/work.js'
// Registers the /pair route. Imported for the side effect, like the other views.
import './galaxy/views/pair.js'
import { requireAuth } from './auth/auth.js'
import { getCurrentProject, setCurrentProject, initProjects, onProjectSwitch } from './project.js'

// Re-export project-picker surface so existing importers (HTML onchange
// shim and other modules that reach into app.ts for getCurrentProject)
// keep working without a rename sweep.
export { getCurrentProject, setCurrentProject, initProjects, onProjectSwitch }

/** Mount the app — call from the HTML template after the DOM is ready. */
export async function mountApp(): Promise<void> {
  // OTel: read key from <meta> tag in index.html (Vercel static deploy).
  // If the tag is missing, honeycombKey is '' and OTel runs as a no-op.
  // Init BEFORE requireAuth so auth errors are captured by the global handlers.
  const honeycombKey =
    document.querySelector('meta[name="honeycomb-ingest-key"]')?.getAttribute('content') ?? ''
  const honeycombDataset =
    document.querySelector('meta[name="honeycomb-dataset"]')?.getAttribute('content') ??
    'grizzly-web'
  initOtel({ honeycombApiKey: honeycombKey, honeycombDataset })

  // Global error boundary — registered early so auth/setup errors are reported.
  window.addEventListener('error', (event) => {
    reportUnhandledError(event.error ?? event.message, 'window.onerror')
  })
  window.addEventListener('unhandledrejection', (event) => {
    reportUnhandledError(
      event.reason instanceof Error ? event.reason : String(event.reason),
      'unhandledrejection',
    )
  })

  if (!(await requireAuth())) {
    return
  }

  // Restore Galaxy atmosphere state before first render
  restoreGalaxy()

  // Render atmosphere overlay (once, never re-rendered)
  const atmoContainer = document.getElementById('atmo-container')
  if (atmoContainer) {
    atmoContainer.innerHTML = renderAtmosphere()
  }

  // Render topbar (hosts #project-picker — must exist before initProjects)
  const topbarContainer = document.getElementById('topbar-container')
  if (topbarContainer) {
    // Authentication succeeded, so the gate is gone: drop the class that hides
    // the shell chrome. Today mountLoginPage is terminal until a reload, so
    // this cannot fire while the gate is up — it is here so that stays true if
    // in-place recovery is ever added.
    document.body.classList.remove('is-login')
    topbarContainer.innerHTML = renderTopbar()
  }

  // Render sidebar
  const sidebarContainer = document.getElementById('sidebar-container')
  if (sidebarContainer) {
    const currentPath = (location.hash || '#/').replace('#', '')
    sidebarContainer.innerHTML = renderSidebar({ activePath: currentPath, galaxyEnabled: true })
  }

  // Ensure #app is focusable and has a landmark role for route-change
  // focus management (WCAG 2.4.3 / SL-UA02)
  const appEl = document.getElementById('app')
  if (appEl) {
    if (!appEl.hasAttribute('tabindex')) {
      appEl.setAttribute('tabindex', '-1')
      appEl.style.outline = 'none'
    }
    if (!appEl.hasAttribute('role')) {
      appEl.setAttribute('role', 'main')
      appEl.setAttribute('aria-label', 'Main content')
    }
  }

  // Create aria-live announcer if not already in HTML template
  if (!document.getElementById('page-title-announcer')) {
    const announcer = document.createElement('div')
    announcer.id = 'page-title-announcer'
    announcer.className = 'sr-only'
    announcer.setAttribute('aria-live', 'polite')
    announcer.setAttribute('aria-atomic', 'true')
    document.body.appendChild(announcer)
  }

  // Initialize project picker BEFORE router — ensures getCurrentProject()
  // returns the correct project ID for the first route dispatch (P1 fix)
  await initProjects()

  // Initialize router and dispatch the initial route
  initRouter()

  // Initialize command palette
  initCommandPalette([
    {
      id: 'fleet',
      label: 'Fleet Overview',
      description: 'View all active and recent runs',
      icon: '\u25A0',
      action: () => {
        navigate('/')
      },
    },
    {
      id: 'work',
      label: 'Work Items',
      description: 'Manage tasks, stories, and epics',
      icon: '\u25A1',
      action: () => {
        navigate('/work')
      },
    },
    {
      id: 'kb',
      label: 'Knowledge Base',
      description: 'Browse decisions and patterns',
      icon: '\u2691',
      action: () => {
        navigate('/kb')
      },
    },
  ])

  // Start fleet SSE (replaces 3s polling)
  startFleetSSE()

  // Debounced SSE reconnect on visibility change to avoid connection storms
  // during rapid tab switching.
  let _sseReconnectTimer: ReturnType<typeof setTimeout> | undefined
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopFleetSSE()
      void flushOtel()
    } else {
      // Debounce reconnect: clear any pending reconnect and wait 300ms
      if (_sseReconnectTimer !== undefined) {
        clearTimeout(_sseReconnectTimer)
      }
      _sseReconnectTimer = setTimeout(() => {
        _sseReconnectTimer = undefined
        startFleetSSE()
      }, 300)
    }
  })

  // Clean up SSE and router on unload
  window.addEventListener('beforeunload', () => {
    stopFleetSSE()
    void flushOtel()
  })

  // Global keyboard shortcuts (Ctrl+K, ?, Esc) and help overlay
  initKeyboardShortcuts()

  // Modal + drawer overlay-click and close-button wiring. Replaces the
  // inline `onclick=""` attributes that previously lived in index.html
  // so the CSP `script-src` directive can drop `'unsafe-inline'`.
  bindModalAndDrawerHandlers()
}

/**
 * Wire up overlay-click-to-close for the detail modal and drawer, and the
 * modal close button. These elements live in index.html (static shell);
 * we attach listeners after mount so the CSP no longer needs unsafe-inline
 * for the inline onclick handlers.
 */
function bindModalAndDrawerHandlers(): void {
  const modal = document.getElementById('grz-modal')
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal && typeof window.closeDetailModal === 'function') {
        window.closeDetailModal()
      }
    })
    const closeBtn = modal.querySelector<HTMLButtonElement>('.grz-modal-close')
    closeBtn?.addEventListener('click', () => {
      window.closeDetailModal?.()
    })
  }
  const drawer = document.getElementById('drawer-overlay')
  if (drawer) {
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer && typeof window.closeDrawer === 'function') {
        window.closeDrawer()
      }
    })
  }
}

// ---------------------------------------------------------------------------
// Expose to window for onclick handlers in HTML
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
// Expose globals for onclick="" handlers and HTML template shims
;(window as any).navigate = navigate
;(window as any).mountApp = mountApp
;(window as any).onProjectSwitch = onProjectSwitch
;(window as any).toggleGalaxy = toggleGalaxy
;(window as any).togSec = togSec
;(window as any).closeDrawer = closeDrawer
;(window as any).switchDrawerTab = switchDrawerTab
;(window as any).copyToClip = copyToClip
// The "Search ⌘K" page-header button fires this via action-delegation.
// Delegate to the real palette module (same function Cmd+K invokes) rather
// than toggling a bare #command-palette element, which used to silently
// no-op because the real palette renders as #cmd-palette-overlay.
;(window as any).openCommandPalette = openCommandPalette
// Work filter setters — invoked by action-delegation handlers in
// src/galaxy/components/action-registry.ts so the filter buttons on the
// Work list page can re-fetch with updated query params.
;(window as any).setWorkTypeFilter = setWorkTypeFilter
;(window as any).setWorkStatusFilter = setWorkStatusFilter
;(window as any).setWorkPriorityFilter = setWorkPriorityFilter
;(window as any).setWorkAssignee = setWorkAssignee
;(window as any).clearWorkFilters = clearWorkFilters
;(window as any).setWorkPage = setWorkPage
;(window as any).toggleWorkTag = toggleWorkTag
;(window as any).setWorkSort = setWorkSort
// The search <input> uses an inline `oninput="window.setWorkSearchInput(this.value)"`
// binding rather than action-delegation so we get a DOM event, not a data-arg.
;(window as any).setWorkSearchInput = setWorkSearchInput
;(window as any).showToast = showToast
;(window as any).esc = esc
;(window as any).renderMd = renderMd
;(window as any).relTime = relTime
;(window as any).codeDiff = codeDiff
;(window as any).clearAuthAndRetry = function () {
  sessionStorage.removeItem('grizzly_auth_token')
  sessionStorage.removeItem('grizzly_auth_state')
  sessionStorage.removeItem('grizzly_auth_state_redirect')
  location.href = location.origin
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// Legacy modal close (backward compat)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(window as any).closeDetailModal = () => {
  document.getElementById('grz-modal')?.style.setProperty('display', 'none')
}

// Initialize SL-UI05 compliant action delegation (must run after window globals are set)
registerAllActions()
initActionDelegation()
